import { useId } from 'react'

// The Promptioso brand: a gradient music mark + wordmark.
//
// The mark is a music note flanked by curved "sound" arcs (the note is playing),
// knocked out of a rounded tile that runs the app's cyan→blue brand gradient
// (#5EE7FF → #1E6FE0, the same ramp as the .btn-primary pill and the `primary`
// palette in tailwind.config.js). Square and symmetric so it stays legible at
// favicon size. Inline SVG so it's crisp at any size and matches the favicon
// (public/icon.svg).
//
// Usage:
//   <Logo />                       full lockup (mark + wordmark)
//   <Logo size="lg" showTagline /> header lockup with "Grammarly for Suno"
//   <Logo mark />                  just the mark (avatars, compact bars)

type Size = 'sm' | 'md' | 'lg'

const MARK_PX: Record<Size, number> = { sm: 24, md: 32, lg: 40 }
const WORD_CLS: Record<Size, string> = {
  sm: 'text-lg',
  md: 'text-xl',
  lg: 'text-2xl',
}

export function LogoMark({ size = 'md', className = '' }: { size?: Size; className?: string }) {
  // Unique gradient ids so multiple marks on one page don't collide.
  const id = useId()
  const fill = `fill-${id}`
  const glass = `glass-${id}`
  const clip = `clip-${id}`
  const px = MARK_PX[size]

  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="Promptioso"
    >
      <defs>
        <linearGradient id={fill} x1="10" y1="10" x2="110" y2="110" gradientUnits="userSpaceOnUse">
          <stop stopColor="#5EE7FF" />
          <stop offset="1" stopColor="#1E6FE0" />
        </linearGradient>
        <linearGradient id={glass} x1="60" y1="6" x2="60" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFFFFF" stopOpacity="0.4" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        <clipPath id={clip}>
          <rect x="6" y="6" width="108" height="108" rx="28" />
        </clipPath>
      </defs>

      {/* matte tile — a barely-there top sheen (no glossy reflection) keeps it
          flat to match the .btn-primary matte pill. */}
      <g clipPath={`url(#${clip})`}>
        <rect x="6" y="6" width="108" height="108" rx="28" fill={`url(#${fill})`} />
        <rect x="6" y="6" width="108" height="34" rx="28" fill={`url(#${glass})`} />
      </g>

      {/* eighth note flanked by curved sound arcs, centred & knocked out in navy */}
      <svg x="14" y="17" width="92" height="86" viewBox="0 0 512 512" fill="#06223A" preserveAspectRatio="xMidYMid meet">
        <path d="M385.91,126.416c-31.093-25.912-83.977-65.666-93.574-78.465H274.13v251.229c-5.767-1.646-11.832-2.578-18.125-2.578c-36.518,0-66.12,29.601-66.12,66.124c0,36.505,29.602,66.115,66.12,66.115c36.514,0,66.115-29.61,66.115-66.115c0,0,0-196.709,0-206.005c0-9.304,4.766-32.256,47.985,10.958c25.594,25.589-16.154,41.662-13.562,83.122l6.098-0.866c7.464-21.475,36.594-37.319,49.186-50.957C432.556,176.517,417.005,152.328,385.91,126.416z" />
        <path d="M140.034,317.705l-31.383-13.706c-7.464,18.676-11.246,38.413-11.246,58.727c0,21.374,4.198,42.137,12.476,61.712c0.093,0.213,0.182,0.425,0.275,0.628l31.366-13.672c-6.557-15.343-9.886-31.705-9.908-48.668C131.615,347.119,134.444,331.988,140.034,317.705z" />
        <path d="M371.967,317.705c5.568,14.267,8.401,29.397,8.418,45.021c0,16.776-3.282,33.053-9.78,48.448c-0.03,0.068-0.059,0.144-0.093,0.22l31.331,13.672c8.465-19.695,12.752-40.653,12.752-62.34c0-20.339-3.774-40.076-11.226-58.745L371.967,317.705z" />
        <path d="M50.67,278.697l-31.34-13.69C6.502,296.025,0,328.883,0,362.726c0,34.554,6.777,68.091,20.369,100.186l0.497,1.137l31.344-13.672c-11.93-27.668-17.982-57.15-17.998-87.65C34.212,333.633,39.75,305.373,50.67,278.697z" />
        <path d="M492.696,265.007l-31.374,13.698c10.911,26.608,16.45,54.861,16.467,84.021c0,29.94-5.862,58.973-17.439,86.353c-0.182,0.432-0.365,0.865-0.547,1.289l31.336,13.664C504.986,432.004,512,397.942,512,362.726C512,328.85,505.499,295.966,492.696,265.007z" />
      </svg>
    </svg>
  )
}

export default function Logo({
  size = 'md',
  mark = false,
  showTagline = false,
  className = '',
}: {
  size?: Size
  /** Render only the mark, no wordmark. */
  mark?: boolean
  showTagline?: boolean
  className?: string
}) {
  if (mark) return <LogoMark size={size} className={className} />

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <LogoMark size={size} />
      <div className="leading-tight">
        <span
          className={`block bg-gradient-to-br from-white via-white to-primary-200 bg-clip-text font-bold tracking-tight text-transparent ${WORD_CLS[size]}`}
        >
          Promptioso
        </span>
        {showTagline && <span className="block text-sm text-gray-500">Grammarly for Suno</span>}
      </div>
    </div>
  )
}
